import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamsApi, gamesApi, eventsApi, usersApi, teamMembersApi } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { UsersIcon, GamepadIcon, CalendarIcon, ShieldIcon, ChartBarIcon, TrophyIcon, SwordsIcon, DumbbellIcon, GraduationIcon, ClipboardIcon, Edit2Icon, TrashIcon } from '../Icons/Icons';
import DateTimeInput from '../DateTimeInput/DateTimeInput';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, canManageTeam, canManageAllTeams } = useAuth();
  const [stats, setStats] = useState({ teams: 0, players: 0, games: 0, events: 0 });
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [sortBy, setSortBy] = useState('name'); // 'name' ou 'mmr'
  const [searchTeam, setSearchTeam] = useState(''); // Recherche d'équipe
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userPlayer, setUserPlayer] = useState(null);
  const [userTeamIds, setUserTeamIds] = useState([]);
  const [hasTeam, setHasTeam] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newEvent, setNewEvent] = useState(null);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [pastEvents, setPastEvents] = useState([]);

  useEffect(() => {
    loadData();
  }, [sortBy]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Chargement des données du dashboard...');

      const [teamsRes, gamesRes, eventsRes, usersRes] = await Promise.all([
        teamsApi.getWithStats({ sort_by: sortBy }).catch(err => {
          console.error('Erreur teams:', err);
          return { data: [] };
        }),
        gamesApi.getAll().catch(err => {
          console.error('Erreur games:', err);
          return { data: [] };
        }),
        eventsApi.getAll().catch(err => {
          console.error('Erreur events:', err);
          return { data: [] };
        }),
        usersApi.getAll({}).catch(err => {
          console.error('Erreur users:', err);
          return { data: [] };
        })
      ]);

      console.log('✅ Données reçues:', {
        teams: teamsRes.data.length,
        games: gamesRes.data.length,
        events: eventsRes.data.length,
        users: usersRes.data.length
      });

      // Charger le profil de l'utilisateur pour TOUS les utilisateurs
      const userProfile = usersRes.data.find(u => u.id === user?.id);
      setUserPlayer(userProfile);

      // Vérifier si l'utilisateur a une équipe (pour les non-managers)
      // On utilise la table team_members (nouvelle méthode) au lieu de user.team_id (legacy)
      if (!canManageAllTeams()) {
        try {
          const userTeamsRes = await teamMembersApi.getUserTeams(user?.id);
          const userTeams = userTeamsRes.data || [];
          setUserTeamIds(userTeams.map(t => t.team_id));
          if (userTeams.length === 0) {
            setHasTeam(false);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error('Erreur lors de la vérification des équipes:', err);
          setHasTeam(false);
          setLoading(false);
          return;
        }
      }

      setStats({
        teams: teamsRes.data.length,
        players: usersRes.data.length,
        games: gamesRes.data.length,
        events: eventsRes.data.length
      });

      setTeams(teamsRes.data);
      setGames(gamesRes.data);

      // Événements à venir et passés
      const now = new Date();
      const upcoming = eventsRes.data
        .filter(e => new Date(e.start_datetime) > now)
        .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))
        .slice(0, 5);

      const past = eventsRes.data
        .filter(e => new Date(e.start_datetime) <= now)
        .sort((a, b) => new Date(b.start_datetime) - new Date(a.start_datetime))
        .slice(0, 5);

      setUpcomingEvents(upcoming);
      setPastEvents(past);

    } catch (error) {
      console.error('❌ Erreur globale:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredTeams = teams.filter(team => {
    // Filtrer par jeu
    if (selectedGame && team.game_id !== parseInt(selectedGame)) {
      return false;
    }
    // Filtrer par recherche
    if (searchTeam && !team.name.toLowerCase().includes(searchTeam.toLowerCase())) {
      return false;
    }
    return true;
  });

  // Vérifier si l'utilisateur fait partie d'une équipe donnée
  const isUserInTeam = (team) => {
    // Les responsables, CEO et admin peuvent voir TOUS les plannings
    if (canManageAllTeams()) {
      return true;
    }

    // Pour les managers, coachs et joueurs : vérifier via la table team_members
    return userTeamIds.includes(team.id);
  };

  const getGameName = (gameId) => {
    const game = games.find(g => g.id === gameId);
    return game ? game.name : 'N/A';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventIcon = (type, size = 18) => {
    const icons = {
      tournament: <TrophyIcon size={size} />,
      scrim: <SwordsIcon size={size} />,
      training: <DumbbellIcon size={size} />,
      coaching: <GraduationIcon size={size} />,
      meeting: <ClipboardIcon size={size} />,
    };
    return icons[type] || <CalendarIcon size={size} />;
  };

  const getEventTypeName = (type) => {
    const names = {
      tournament: 'Tournoi',
      scrim: 'Scrim',
      training: 'Entraînement',
      coaching: 'Coaching',
      meeting: 'Réunion',
    };
    return names[type] || type;
  };

  const handleEditEvent = (event) => {
    setEditingEvent({
      ...event,
      start_datetime: new Date(event.start_datetime).toISOString().slice(0, 16),
      end_datetime: new Date(event.end_datetime).toISOString().slice(0, 16)
    });
    setShowEditModal(true);
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    try {
      // S'assurer que team_ids est synchronisé avec team_id
      const updateData = {
        ...editingEvent,
        team_ids: editingEvent.team_id ? [editingEvent.team_id] : []
      };
      await eventsApi.update(editingEvent.id, updateData);
      setShowEditModal(false);
      setEditingEvent(null);
      loadData();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour de l\'événement');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cet événement ?')) {
      return;
    }
    try {
      await eventsApi.delete(eventId);
      loadData();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression de l\'événement');
    }
  };

  const handleCreateEvent = (teamId = null, gameId = null) => {
    const now = new Date();
    const startTime = new Date(now.getTime() + 60 * 60 * 1000); // +1h
    const endTime = new Date(startTime.getTime() + 2 * 60 * 60 * 1000); // +2h

    setNewEvent({
      title: '',
      type: 'training',
      team_id: teamId,
      game_id: gameId,
      team_ids: teamId ? [teamId] : [],
      scope: teamId ? 'single' : 'all', // 'single', 'game', 'all'
      is_online: 1,
      location: '',
      start_datetime: startTime.toISOString().slice(0, 16),
      end_datetime: endTime.toISOString().slice(0, 16),
      notes: ''
    });
    setShowCreateModal(true);
  };

  const handleSubmitNewEvent = async (e) => {
    e.preventDefault();
    try {
      // Préparer les données selon le scope
      const eventData = { ...newEvent };

      if (newEvent.scope === 'all') {
        // Événement pour toutes les équipes
        eventData.team_ids = teams.map(t => t.id);
        eventData.team_id = teams[0]?.id || null;
      } else if (newEvent.scope === 'game') {
        // Événement pour toutes les équipes d'un jeu
        const gameTeams = teams.filter(t => t.game_id === newEvent.game_id);
        eventData.team_ids = gameTeams.map(t => t.id);
        eventData.team_id = gameTeams[0]?.id || null;
      } else {
        // Événement pour une seule équipe
        eventData.team_ids = [newEvent.team_id];
      }

      delete eventData.scope;
      delete eventData.game_id;

      await eventsApi.create(eventData);
      setShowCreateModal(false);
      setNewEvent(null);
      loadData();
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      alert('Erreur lors de la création de l\'événement');
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <h1>Chargement...</h1>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <h1>Erreur</h1>
        <div className="error-box">
          <p>{error}</p>
          <button onClick={loadData} className="btn-primary">Réessayer</button>
        </div>
      </div>
    );
  }

  // Si le joueur n'a pas d'équipe
  if (!loading && !hasTeam && !canManageTeam()) {
    return (
      <div className="dashboard">
        <h1>Bienvenue sur TSK Arena</h1>
        <div className="info-box">
          <span className="info-icon">⚠️</span>
          <div>
            <h2>Aucune équipe assignée</h2>
            <p>Un manager doit vous ajouter dans une équipe pour accéder aux contenus de l'application.</p>
            <p>Veuillez contacter un administrateur ou un manager pour rejoindre une équipe.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <h1>Bienvenue {user?.username}</h1>

      <div className="stats-grid">
        <div className="stat-card">
          <span className="icon"><ShieldIcon size={32} /></span>
          <div>
            <div className="value">{stats.teams}</div>
            <div className="label">Équipes</div>
          </div>
        </div>

        <div className="stat-card">
          <span className="icon"><UsersIcon size={32} /></span>
          <div>
            <div className="value">{stats.players}</div>
            <div className="label">Membres</div>
          </div>
        </div>

        <div className="stat-card">
          <span className="icon"><GamepadIcon size={32} /></span>
          <div>
            <div className="value">{stats.games}</div>
            <div className="label">Jeux</div>
          </div>
        </div>

        <div className="stat-card">
          <span className="icon"><CalendarIcon size={32} /></span>
          <div>
            <div className="value">{stats.events}</div>
            <div className="label">Événements</div>
          </div>
        </div>
      </div>

      <div className="content-grid">
        <div className="section">
          <div className="section-header">
            <h2>Équipes ({filteredTeams.length})</h2>
            <div className="filters-group">
              <input
                type="text"
                placeholder="🔍 Rechercher une équipe..."
                value={searchTeam}
                onChange={(e) => setSearchTeam(e.target.value)}
                className="search-filter"
              />
              <select
                value={selectedGame || ''}
                onChange={(e) => setSelectedGame(e.target.value || null)}
                className="game-filter"
              >
                <option value="">Tous les jeux</option>
                {games.map(game => (
                  <option key={game.id} value={game.id}>{game.name}</option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-filter"
              >
                <option value="name">Trier par nom</option>
                <option value="mmr">Trier par MMR</option>
              </select>
            </div>
          </div>

          <div className="teams-list">
            {filteredTeams.length === 0 ? (
              <p className="empty-state">
                Aucune équipe. <a href="/teams">Créer une équipe</a>
              </p>
            ) : (
              filteredTeams.map(team => (
                <div key={team.id} className="team-item">
                  <div className="team-item-top">
                    {team.average_mmr && (
                      <div className="team-mmr-badge-dash">{Math.round(team.average_mmr)}</div>
                    )}
                    <div className="team-info">
                      <h3>{team.name}</h3>
                      <div className="team-meta">
                        <span className="team-game">{getGameName(team.game_id)}</span>
                        {team.coach && <span className="team-coach">· {team.coach}</span>}
                      </div>
                    </div>
                  </div>
                  {isUserInTeam(team) && (
                    <div className="team-actions">
                      <button
                        className="btn-planning"
                        onClick={() => navigate(`/calendrier?team=${team.id}`)}
                      >
                        <CalendarIcon size={14} /> Planning
                      </button>
                      <button
                        className="btn-stats"
                        onClick={() => navigate(`/equipes/${team.id}/statistiques`)}
                      >
                        <ChartBarIcon size={14} /> Stats
                      </button>
                      {canManageTeam() && (
                        <button
                          className="btn-create-event"
                          onClick={() => handleCreateEvent(team.id, team.game_id)}
                        >
                          + Événement
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="section">
          <div className="section-header">
            <h2>{showPastEvents ? 'Événements passés' : 'Événements à venir'}</h2>
            <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
              <button
                className="btn-secondary btn-sm"
                onClick={() => setShowPastEvents(!showPastEvents)}
              >
                {showPastEvents ? <><CalendarIcon size={14} /> À venir</> : <><ClipboardIcon size={14} /> Passés</>}
              </button>
              {canManageTeam() && (
                <button
                  className="btn-create-global"
                  onClick={() => handleCreateEvent()}
                  title="Créer un événement global"
                >
                  + Nouvel événement
                </button>
              )}
            </div>
          </div>
          <div className="events-list">
            {(showPastEvents ? pastEvents : upcomingEvents).length === 0 ? (
              <p className="empty-state">{showPastEvents ? 'Aucun événement passé' : 'Aucun événement prévu'}</p>
            ) : (
              (showPastEvents ? pastEvents : upcomingEvents).map(event => {
                const eventType = getEventTypeName(event.type);
                const isMultiTeam = event.team_ids && event.team_ids.length > 1;

                // Pour les événements multi-équipes, ne pas afficher une équipe spécifique
                const eventTeam = !isMultiTeam ? teams.find(t => t.id === event.team_id) : null;
                const affectedTeams = isMultiTeam
                  ? teams.filter(t => event.team_ids.includes(t.id))
                  : [];

                return (
                  <div key={event.id} className="event-item">
                    <div className="event-type">{getEventIcon(event.type)}</div>
                    <div className="event-details">
                      <h4>{event.title}</h4>
                      <div className="event-info">
                        <span className="event-label">{eventType}</span>
                        {isMultiTeam ? (
                          <span className="event-team multi">
                            {affectedTeams.length === teams.length
                              ? '🌐 Toutes les équipes'
                              : (() => {
                                  // Détecter si c'est toutes les équipes d'un jeu spécifique
                                  const gameIds = [...new Set(affectedTeams.map(t => t.game_id))];
                                  if (gameIds.length === 1 && gameIds[0]) {
                                    const game = games.find(g => g.id === gameIds[0]);
                                    const gameTeamsCount = teams.filter(t => t.game_id === gameIds[0]).length;
                                    if (affectedTeams.length === gameTeamsCount && game) {
                                      return `🎮 Toutes les équipes ${game.name}`;
                                    }
                                  }
                                  return `🌐 ${affectedTeams.length} équipe${affectedTeams.length > 1 ? 's' : ''}`;
                                })()}
                          </span>
                        ) : eventTeam ? (
                          <span className="event-team">📍 {eventTeam.name}</span>
                        ) : null}
                      </div>
                      <span className="event-date">{formatDate(event.start_datetime)}</span>
                      {event.notes && <p className="event-notes">{event.notes}</p>}
                    </div>
                    {canManageTeam() && (
                      <div className="event-actions">
                        <button
                          className="btn-edit-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditEvent(event);
                          }}
                          title="Modifier"
                        >
                          <Edit2Icon size={18} />
                        </button>
                        <button
                          className="btn-delete-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEvent(event.id);
                          }}
                          title="Supprimer"
                        >
                          <TrashIcon size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {showEditModal && editingEvent && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setShowEditModal(false)}
              type="button"
              aria-label="Fermer"
            >
              ✕
            </button>
            <h2>Modifier l'événement</h2>
            <form onSubmit={handleUpdateEvent}>
              <div className="form-group">
                <label>Titre</label>
                <input
                  type="text"
                  value={editingEvent.title}
                  onChange={(e) => setEditingEvent({...editingEvent, title: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Type</label>
                <select
                  value={editingEvent.type}
                  onChange={(e) => setEditingEvent({...editingEvent, type: e.target.value})}
                  required
                >
                  <option value="tournament">Tournoi</option>
                  <option value="scrim">Scrim</option>
                  <option value="training">Entraînement</option>
                  <option value="coaching">Coaching</option>
                  <option value="meeting">Réunion</option>
                </select>
              </div>

              <div className="form-group">
                <label>Équipe</label>
                <select
                  value={editingEvent.team_id}
                  onChange={(e) => setEditingEvent({...editingEvent, team_id: parseInt(e.target.value)})}
                  required
                >
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <DateTimeInput
                  label="Début"
                  value={editingEvent.start_datetime}
                  onChange={(val) => setEditingEvent({...editingEvent, start_datetime: val})}
                  required
                />
                <DateTimeInput
                  label="Fin"
                  value={editingEvent.end_datetime}
                  onChange={(val) => setEditingEvent({...editingEvent, end_datetime: val})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={editingEvent.notes || ''}
                  onChange={(e) => setEditingEvent({...editingEvent, notes: e.target.value})}
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateModal && newEvent && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setShowCreateModal(false)}
              type="button"
              aria-label="Fermer"
            >
              ✕
            </button>
            <h2>Créer un événement</h2>
            <form onSubmit={handleSubmitNewEvent}>
              <div className="form-group">
                <label>Titre</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                  placeholder="Nom de l'événement"
                  required
                />
              </div>

              <div className="form-group">
                <label>Type</label>
                <select
                  value={newEvent.type}
                  onChange={(e) => setNewEvent({...newEvent, type: e.target.value})}
                  required
                >
                  <option value="tournament">Tournoi</option>
                  <option value="scrim">Scrim</option>
                  <option value="training">Entraînement</option>
                  <option value="coaching">Coaching</option>
                  <option value="meeting">Réunion</option>
                </select>
              </div>

              <div className="form-group">
                <label>Portée de l'événement</label>
                <select
                  value={newEvent.scope}
                  onChange={(e) => setNewEvent({...newEvent, scope: e.target.value})}
                  required
                >
                  <option value="single">Une seule équipe</option>
                  <option value="game">Toutes les équipes d'un jeu</option>
                  <option value="all">Toutes les équipes</option>
                </select>
              </div>

              {newEvent.scope === 'single' && (
                <div className="form-group">
                  <label>Équipe</label>
                  <select
                    value={newEvent.team_id || ''}
                    onChange={(e) => setNewEvent({...newEvent, team_id: parseInt(e.target.value)})}
                    required
                  >
                    <option value="">Sélectionner une équipe</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {newEvent.scope === 'game' && (
                <div className="form-group">
                  <label>Jeu</label>
                  <select
                    value={newEvent.game_id || ''}
                    onChange={(e) => setNewEvent({...newEvent, game_id: parseInt(e.target.value)})}
                    required
                  >
                    <option value="">Sélectionner un jeu</option>
                    {games.map(game => (
                      <option key={game.id} value={game.id}>{game.name}</option>
                    ))}
                  </select>
                  <small style={{color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block'}}>
                    {newEvent.game_id && `${teams.filter(t => t.game_id === newEvent.game_id).length} équipe(s) concernée(s)`}
                  </small>
                </div>
              )}

              {newEvent.scope === 'all' && (
                <div className="form-group">
                  <small style={{color: 'var(--text-muted)', fontSize: '0.85rem'}}>
                    Cet événement sera visible pour toutes les équipes.
                  </small>
                </div>
              )}

              <div className="form-group">
                <label>Format</label>
                <select
                  value={newEvent.is_online ? 'online' : 'offline'}
                  onChange={(e) => setNewEvent({...newEvent, is_online: e.target.value === 'online' ? 1 : 0, location: e.target.value === 'online' ? '' : newEvent.location})}
                >
                  <option value="online">En ligne</option>
                  <option value="offline">Présentiel</option>
                </select>
              </div>

              {!newEvent.is_online && (
                <div className="form-group">
                  <label>Lieu</label>
                  <input
                    type="text"
                    value={newEvent.location || ''}
                    onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                    placeholder="Adresse ou lien Google Maps / Mappy..."
                  />
                </div>
              )}

              <div className="form-row">
                <DateTimeInput
                  label="Début"
                  value={newEvent.start_datetime}
                  onChange={(val) => setNewEvent({...newEvent, start_datetime: val})}
                  required
                />
                <DateTimeInput
                  label="Fin"
                  value={newEvent.end_datetime}
                  onChange={(val) => setNewEvent({...newEvent, end_datetime: val})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newEvent.notes || ''}
                  onChange={(e) => setNewEvent({...newEvent, notes: e.target.value})}
                  placeholder="Informations complémentaires..."
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowCreateModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  Créer l'événement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
